define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"csi",
	"csi/heading/Attribute",
	"csi/heading/_base",
	"csi/heading/_EnumMixin",
	"csi/enums",
	"csi/html",
	"csi/middleware/common",
	"csi/middleware/inline/InlinePopupService",
	"csi/widget/CheckedMultiSelectDropdown",
	"csi/widget/InlineSelect",
	"csi/locale",
	"csi/xml"
], function(declare, lang, domClass, csi, Attribute, csiHeading, _EnumMixin, enums, html, {convertNamesStoreDataToComponentItems},
	{CONTROL_TYPE_SINGLE_SELECT}, CheckedMultiSelectDropdown, InlineSelect, locale, xml) {

	var KPIs = {
		KpiColor: true,
		KpiColor2: true,
		KpiColor3: true,
		KpiComplete: true,
		KpiTrendDown: true,
		KpiTrendUp: true
	};

	const mapKpiIds = {
		KpiColor2: 'KpiColor',
		KpiColor3: 'KpiColor',
	};

	function _isInvalid(/*String*/format, /*String*/value) {
		var store = format ? enums.getStore(format) : enums.getStore(xml.enumGetId(value));
		var isValid;
		if (store) {
			if (value) {
				isValid = store.get(value);
			} else {
				isValid = true;
			}
		}
		return !isValid;
	}

	/**
	 * Check the enum value is valid or not
	 * @param {string} value - value of enum
	 * @param {string} fmt - enum format
	 * @return {string} -  additional info if enum is invalid
	 */
	function _additionalInfo(value, fmt) {
		return _isInvalid(fmt, value) ? "(" + locale.resource("ActionEnum_Invalid") + ")" : "";
	}

	return declare([Attribute, _EnumMixin], {
		queryOps: csiHeading.QUERY_OPS[xml.ATTR_ENUM],
		tagAction: "ActionEnum",
		type: xml.ATTR_ENUM,

		/** @property {boolean} capitalized - set to true if rendered as the capitalized text */
		capitalized: false,

		getDefaultValue: function(/*Heading*/h) {
			// summary: return default enum value
			return xml.enumCreate(h.m_format);
		},

		getQuerySpec: function(/*Context*/ctx, /*Heading*/h, /*Spec*/spec) {
			// summary: return Spec.querySpec for given heading
			var qs = this.inherited(arguments);
			qs.allowFilteringBySelectionOfExistingValues = true;
			qs.blanksOperator = xml.OP_BLANK_ENUM;
			return qs;
		},

		/**
		 * @override
		 * Return localized name for enum value
		 */
		formatValue(value, fmt) {
			return locale.getEnumName(value) || "";
		},

		/**
		 * @override
		 * Don't enable the ability to copy if the value is inactive
		 */
		canCopy: function(ctxCopy, h) {
			return this.inherited(arguments) && !_isInvalid(h.m_format, this._getValue(ctxCopy, h));
		},

		/**
		 * @override
		 * if enum is invalid and bSelected is true, then add additional info to value
		 */
		formatQueryFilterValue: function(ctx, value, format, bFloatingDates, bSelected) {
			return this.formatValue(value, format) + (bSelected ? _additionalInfo(value, format) : "");
		},

		/**
		 * @override
		 * If uses Heading Dependency then use normal behavior; otherwise check for valid enum dependency
		 */
		hasValidDependency(ctx, h) {
			return h.m_dependency ? this.inherited(arguments) : this.enumHasValidDependsOn(ctx, h);
		},

		isIcon: function(/*String*/enumId) {
			// summary: return if enum should be rendered as icon - by default, do so for KPI enums
			return KPIs[enumId];
		},

		toFilterValueCSS: function(/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: if enum is no longer in EnumList, then style in filter drop-down
			return _isInvalid(this._getFormat(ctx, h), value) ? html.CSS_DISABLED_COLOR : null;
		},

		/**
		 * Check enum is in the store or not
		 * @param {object} ctx - context of the enum
		 * @param {object} h  - heading info of the enum
		 * @param {string} value -  enum value
		 * @return {string} - invalid message
		 */
		toFilterValueExtra: function(ctx, h, value) {
			// summary: if enum is no longer in EnumList, add extra information next to value in filter drop-down
			return _isInvalid(this._getFormat(ctx, h), value) ? ' (' + locale.resource("ActionEnum_Invalid") + ')' : null;
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: if KPI enum, then render icon rather than text
			var value = this._getValue(ctx, h);
			var cls = this._getIconClass(value);
			if (cls) {
				domClass.add(refNode, "attrIcon");
				html.create("span", {"class": cls + " dijitInline " + html.CSS_NO_EXPORT}, refNode);
				refNode = html.create("span", {"class": html.CSS_EXPORT}, refNode);
			}

			if (this.capitalized) {
				let enumText = this.formatValue(value, this._getFormat(ctx, h));
				if ((enumText != null) && (enumText !== "")) {
					enumText = locale.capitalizeText(enumText);
					html.text(refNode, enumText);
					return true;
				}

				return false;
			}

			return this.inherited(arguments);
		},

		toHtmlDynamic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Object*/params) {
			// summary: create store for enumeration values and select control to edit
			if (params.value == null) {
				params.value = this._getValue(ctx, h) || this.getDefaultValue(h);
			}

			if (params.store == null) {
				params.store = this.getStore(ctx, h, params.value);
			}

			var fmt = this._getFormat(ctx, h);

			if (this.isIcon(fmt)) {
				params.labelType = "html";
				params.labelFunc = lang.hitch(this, this._getIconLabel, h);
			}

			return new InlineSelect(params);
		},

		/**
		 * @override
		 * @returns widget for specifying query filter for attributes of this type and operator
		 */
		toHtmlQueryFilter: function(ctx, spec, refNode, params, op) {
			var h = spec.heading;

			if (params.store == null) {
				params.store = enums.getStore(this._getFormat(ctx, h), params.value);
			}

			params.tooltipPosition = ["above-centered"];
			params.showInvalidMessage = locale.resource("ActionEnum_Invalid");
			params.showInvalidMessageAttr = "invalid";
			// DE20982 - do not reuse toHtmlDynamic as EnumAction will overwrite params.value
			if (op === xml.OP_EQUALS || op === xml.OP_NOT_EQUALS) {
				return new InlineSelect(params);
			} else if (op === xml.OP_CONTAINS || op === xml.OP_NOT_CONTAINS) {
				return new CheckedMultiSelectDropdown(params);
			}
		},

		_getIconClass: function(/*String*/value) {
			// summary: return classes used to render icon
			var enumId = xml.enumGetId(value);

			if (this.isIcon(enumId)) {
				var s = xml.enumGetText(value);
				if (s) {
					return "csiActionIcon icon" + (mapKpiIds[enumId] || enumId) + s;
				}
			}
			return null;
		},

		_getIconLabel: function(/*Heading*/h, /*Object*/item, /*Store*/store) {
			// summary: render icon
			var cls = this._getIconClass(item[store.idProperty]);
			if (!cls) {
				return csi.EMPTY_CHAR;
			}

			var s = '<span class="' + cls + '"></span>';

			// this is necessary in case need to apply additional CSS to
			// all icons (such as if they reside in a different image sprite
			// than the default)
			if (h.m_class) {
				s = '<span class="' + h.m_class + '">' + s + '</span>';
			}

			return s;
		},

		_toSortValue: function(/*Any*/value) {
			// summary: return index of enum value in list
			var store = enums.getStore(xml.enumGetId(value));
			var item = store && store.get(value);
			return item ? item[store.indexProperty] : enums.getIndexForOldEnum(locale.getEnumName(value));
		},

		/** React */

		/**
		 * @override
		 */
		getReactControlOptions: async function(store, controller) {
			const data = await store.query();
			const options = convertNamesStoreDataToComponentItems(data, store);
			// if value is "required" then empty value will be removed from dropdown and therefore dropdown value can't be initialized to empty url, 
			// in this case it must be null or assertion will happen and shown in console
			const {oldValue} = controller;
			let value;
			if (controller.isValueRequired() && !xml.enumGetText(oldValue)) {
				value = null;
			} else {
				value = options.find(({id}) => id === oldValue);
			}
			return {options, store, value};
		},

		/**
		 * @override
		 */
		getReactPlaceholderOptions: function(controller, ctx, h) {
			const {oldValue} = controller;
			const store = this.getStore(ctx, h, oldValue);
			if (!oldValue || !xml.enumGetText(oldValue)) {
				return {options: [], value: null, store};
			}
			const data = store.getFromMemory ? [store.getFromMemory(oldValue)] : [];
			const options = convertNamesStoreDataToComponentItems(data, store);
			const value = options.find(({id}) => id === oldValue);

			return {options, value, store};
		},

		/**
		 * @param {context} ctx
		 * @return {string}
		 */
		getReactControlType: function(ctx, h) {
			var fmt = this._getFormat(ctx, h);
			if (this.isIcon(fmt)) {
				return "csi/middleware/inline/EnumIconInlineSelect";
			}
			return CONTROL_TYPE_SINGLE_SELECT;
		},

		/** 
		 * return true if react renderer exists for given attribute type and should be used
		 */
		useReactInlineDialog() {
			return true;
		},

	});
});